/**
 * stageProgramAdapter — `GET /v1/stage-program` (and the answer of `POST …/adapt`) → `UIStageProgram`.
 *
 * API-aware: knows the payload of `FamilyProgramAdjustment::payload()`. Day grouping is the
 * view-model's (`stageProgramViewModel`). Not imported by the DS.
 */
import { formatDateRange, formatHour } from "../../../ui/formatters/dateFormatter"
import {
    type UIRevisionProgram,
    type UIRevisionProgramChip,
    type UIStageProgram,
    type UIStageProgramState,
} from "../../../ui/types"
import { toUISubject, type ApiSchoolSubject } from "../shared/schoolSubjectAdapter"

export type ApiStageProgramItem = {
    day: number
    date: string | null
    time: string | null
    chapter_id: number
    chapter_name: string
    is_previous_class: boolean
    subject: ApiSchoolSubject | null
}

export type ApiStageProgram = {
    study_program_id: number
    /** The formula bought — null when its template has no name. */
    stage_title: string | null
    based_on_study_program_id: number | null
    class: string | null
    previous_class: string | null
    starts_on: string | null
    ends_on: string | null
    state: UIStageProgramState
    instructions: string | null
    items: ApiStageProgramItem[]
}

/** The chapters a programme holds — what a recomposition compares to tell what is new. */
export function stageProgramChapterIds(raw: ApiStageProgram | null | undefined): number[] {
    return (raw?.items ?? []).map((item) => item.chapter_id)
}

/** The séance's hour as the parent space writes it (« 10h00 ») — the server only gives it with a date. */
function toHour(item: ApiStageProgramItem): string | undefined {
    return item.date && item.time ? formatHour(`${item.date}T${item.time}`) : undefined
}

function chipsOf(item: ApiStageProgramItem, raw: ApiStageProgram, isNew: boolean): UIRevisionProgramChip[] | undefined {
    const chips: UIRevisionProgramChip[] = []
    if (item.is_previous_class && raw.previous_class)
        chips.push({ text: `rappel de ${raw.previous_class}`, tone: "neutral" })
    if (isNew) chips.push({ text: "selon votre demande", tone: "accent" })
    return chips.length > 0 ? chips : undefined
}

function toUIProgram(
    item: ApiStageProgramItem,
    raw: ApiStageProgram,
    newChapterIds: ReadonlySet<number>
): UIRevisionProgram {
    return {
        id: `${item.day}-${item.chapter_id}`,
        title: item.chapter_name,
        className: raw.class ?? "",
        subject: toUISubject(item.subject),
        date: item.date ?? undefined,
        day: item.day,
        time: toHour(item),
        chips: chipsOf(item, raw, newChapterIds.has(item.chapter_id)),
    }
}

/**
 * The séances, one card per chapter, in the order of the week.
 *
 * @param newChapterIds the chapters the last recomposition brought — highlighted « selon votre
 *        demande » until the page is left.
 */
export function toUIStageProgramItems(
    raw: ApiStageProgram,
    newChapterIds: ReadonlySet<number> = new Set()
): UIRevisionProgram[] {
    return raw.items.map((item) => toUIProgram(item, raw, newChapterIds))
}

/** The page's head: whose programme, when, with whom, where the family stands, the mix of classes. */
export function toUIStageProgramSummary(
    raw: ApiStageProgram,
    firstname: string | null | undefined
): Omit<UIStageProgram, "groups"> {
    const previous = raw.items.filter((item) => item.is_previous_class).length

    return {
        stageTitle: raw.stage_title || "Stage de vacances",
        title: firstname ? `Le programme de ${firstname}` : "Le programme du stage",
        childFirstname: firstname ?? null,
        dates: raw.starts_on && raw.ends_on ? formatDateRange(raw.starts_on, raw.ends_on) : null,
        state: raw.state,
        instructions: raw.instructions,
        mix:
            previous > 0 && raw.previous_class && raw.class
                ? {
                      previous,
                      own: raw.items.length - previous,
                      previousLabel: `Bases de ${raw.previous_class}`,
                      ownLabel: `Début de ${raw.class}`,
                  }
                : null,
    }
}
